import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdAssignment } from "react-icons/md";
import * as Yup from "yup";
import { useFormik } from "formik";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { NotifyContext } from "../../context/notifyContext";
import CourseService from "../../services/api/CourseService";
import {
  EducationalLevel,
  EducationalSystem,
  Exercise,
  Question,
  QuestionType,
} from "../../Types/Courses";
import Button from "../../UI/Button/Button";
import ButtonLoading from "../../UI/Button/ButtonLoading";
import InputField from "../../UI/Form/InputField";

const questionTypes: QuestionType[] = [
  "multiple_choice",
  "single_choice",
  "short_answer",
  "number",
];

interface QuestionFormState {
  id?: number;
  type: QuestionType;
  question_text: string;
  points: number;
  choices: string[];
  correct_choice_index: number;
  correct_value: string;
}

const defaultQuestionState: QuestionFormState = {
  type: "single_choice",
  question_text: "",
  points: 1,
  choices: ["", ""],
  correct_choice_index: 0,
  correct_value: "",
};

export default function ExerciseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  const [saving, setSaving] = useState(false);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [systems, setSystems] = useState<EducationalSystem[]>([]);
  const [levels, setLevels] = useState<EducationalLevel[]>([]);
  const [questionForm, setQuestionForm] = useState<QuestionFormState>(defaultQuestionState);
  const [questionLoading, setQuestionLoading] = useState(false);

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    {
      label: "Exercices",
      icon: <MdAssignment />,
      is_link: true,
      href: "/app/courses/exercises",
    },
    { label: id ? `Édition #${id}` : "Nouveau", is_link: false },
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: exercise?.title || "",
      description: exercise?.description || "",
      educational_system_id: exercise?.educational_system_id || "",
      level_id: exercise?.level_id || "",
      is_global: exercise?.is_global ?? true,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required("Le titre est obligatoire"),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const payload = {
          title: values.title,
          description: values.description || null,
          educational_system_id: values.educational_system_id
            ? Number(values.educational_system_id)
            : null,
          level_id: values.level_id ? Number(values.level_id) : null,
          is_global: values.is_global,
        };

        if (id) {
          await CourseService.updateExercise(Number(id), payload);
          notify("Exercice mis à jour avec succès");
        } else {
          const response: any = await CourseService.createExercise(payload);
          notify("Exercice créé avec succès");
          const created = response?.data?.exercise;
          if (created?.id) navigate(`/app/courses/exercises/${created.id}/edit`);
        }
      } catch (error: any) {
        notify(error?.message || error || "Erreur lors de l'enregistrement", "error");
      } finally {
        setSaving(false);
      }
    },
  });

  const selectedSystemId = Number(formik.values.educational_system_id) || 0;

  const filteredLevels = useMemo(
    () => levels.filter((item) => item.educational_system_id === selectedSystemId),
    [levels, selectedSystemId]
  );

  const loadExercise = async () => {
    if (!id) return;
    const response: any = await CourseService.getExercise(Number(id));
    const data = response?.data?.exercise;
    setExercise(data || null);
    setQuestions(data?.questions || []);
  };

  const loadEducationParams = async () => {
    const response: any = await CourseService.getEducationParams();
    const systemsData = response?.data?.systems || [];
    setSystems(systemsData);
    const flatLevels = systemsData.flatMap((system: EducationalSystem) => system.levels || []);
    setLevels(flatLevels);
  };

  useEffect(() => {
    loadEducationParams().catch((error) => {
      notify(error?.message || error || "Impossible de charger les systèmes éducatifs", "error");
    });
  }, []);

  useEffect(() => {
    if (!id) return;
    loadExercise().catch((error) => {
      notify(error?.message || error || "Impossible de charger l'exercice", "error");
    });
  }, [id]);

  const normalizeAsObject = (value: unknown): Record<string, unknown> => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value) as Record<string, unknown>;
      } catch (error) {
        return {};
      }
    }
    return value as Record<string, unknown>;
  };

  const handleSubmitQuestion = async () => {
    if (!id) {
      notify("Enregistre d'abord l'exercice avant d'ajouter des questions", "warning");
      return;
    }
    if (!questionForm.question_text.trim()) {
      notify("Le texte de la question est obligatoire", "error");
      return;
    }
    setQuestionLoading(true);
    try {
      let options: Record<string, unknown> | undefined;
      let correct_answer: Record<string, unknown>;

      if (questionForm.type === "single_choice" || questionForm.type === "multiple_choice") {
        const choices = questionForm.choices.map((item) => item.trim()).filter(Boolean);
        if (choices.length < 2) {
          throw new Error("Ajoute au moins 2 choix pour une question à choix");
        }
        if (questionForm.correct_choice_index >= choices.length) {
          throw new Error("Sélectionne une réponse correcte valide");
        }
        options = { choices };
        correct_answer = { index: questionForm.correct_choice_index };
      } else if (questionForm.type === "number") {
        if (questionForm.correct_value === "") {
          throw new Error("La réponse correcte numérique est obligatoire");
        }
        correct_answer = { value: Number(questionForm.correct_value) };
      } else {
        if (!questionForm.correct_value.trim()) {
          throw new Error("La réponse correcte est obligatoire");
        }
        correct_answer = { value: questionForm.correct_value.trim() };
      }

      const payload = {
        type: questionForm.type,
        question_text: questionForm.question_text.trim(),
        options,
        correct_answer,
        points: Number(questionForm.points) || 1,
      };
      if (questionForm.id) {
        await CourseService.updateQuestion(Number(id), questionForm.id, payload);
        notify("Question mise à jour");
      } else {
        await CourseService.createQuestion(Number(id), payload);
        notify("Question ajoutée");
      }
      setQuestionForm(defaultQuestionState);
      await loadExercise();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de l'enregistrement de la question", "error");
    } finally {
      setQuestionLoading(false);
    }
  };

  const editQuestion = (question: Question) => {
    const options = normalizeAsObject(question.options);
    const correctAnswer = normalizeAsObject(question.correct_answer);
    const choices = Array.isArray(options.choices)
      ? (options.choices as unknown[]).map((item) => String(item))
      : ["", ""];

    setQuestionForm({
      id: question.id,
      type: question.type,
      question_text: question.question_text || "",
      points: question.points || 1,
      choices,
      correct_choice_index: Number(correctAnswer.index || 0),
      correct_value:
        question.type === "number"
          ? String(correctAnswer.value || "")
          : String(correctAnswer.value || ""),
    });
  };

  const deleteQuestion = async (question: Question) => {
    if (!id) return;
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await CourseService.deleteQuestion(Number(id), question.id);
      notify("Question supprimée");
      await loadExercise();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la suppression", "error");
    }
  };

  const reorderQuestion = async (direction: "up" | "down", question: Question) => {
    if (!id) return;
    const index = questions.findIndex((item) => item.id === question.id);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === questions.length - 1)
    ) {
      return;
    }

    const reordered = [...questions];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

    try {
      await CourseService.reorderQuestions(
        Number(id),
        reordered.map((item) => item.id)
      );
      await loadExercise();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors du réordonnancement", "error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Éditeur d'exercice</h2>
          <p className="text-sm text-gray-600">
            Création guidée des questions sans JSON manuel.
          </p>
        </div>
        <div className="flex gap-2">
          {id ? (
            <Button onClick={() => navigate(`/app/courses/exercises/${id}/details`)}>
              Voir les stats
            </Button>
          ) : null}
          <Button onClick={() => navigate("/app/courses/exercises")}>Retour liste</Button>
        </div>
      </div>

      <div className="bg-white rounded shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Informations de l'exercice</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Titre</label>
            <InputField
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.title && formik.errors.title ? (
              <p className="text-xs text-red-500">{formik.errors.title}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Système éducatif</label>
            <select
              className="w-full form-select"
              name="educational_system_id"
              value={formik.values.educational_system_id}
              onChange={(event) => {
                formik.handleChange(event);
                formik.setFieldValue("level_id", "");
              }}
            >
              <option value="">-- Sélectionner --</option>
              {systems.map((system) => (
                <option key={system.id} value={system.id}>
                  {system.title}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Niveau</label>
            <select
              className="w-full form-select"
              name="level_id"
              value={formik.values.level_id}
              onChange={formik.handleChange}
            >
              <option value="">-- Sélectionner --</option>
              {filteredLevels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.title}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Portée</label>
            <select
              className="w-full form-select"
              name="is_global"
              value={formik.values.is_global ? "true" : "false"}
              onChange={(event) =>
                formik.setFieldValue("is_global", event.target.value === "true")
              }
            >
              <option value="true">Global</option>
              <option value="false">Spécifique</option>
            </select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              className="w-full form-input"
              rows={4}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <ButtonLoading
            loading={saving}
            label={id ? "Mettre à jour l'exercice" : "Créer l'exercice"}
            onClick={() => formik.handleSubmit()}
          />
        </div>
      </div>

      <div className="bg-white rounded shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Questions</h3>
          <Button color="primary" onClick={() => setQuestionForm(defaultQuestionState)}>
            Nouvelle question
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded p-3 mb-4">
          <div>
            <label className="text-sm font-medium">Type</label>
            <select
              className="w-full form-select"
              value={questionForm.type}
              onChange={(event) =>
                setQuestionForm((prev) => ({
                  ...prev,
                  type: event.target.value as QuestionType,
                }))
              }
            >
              {questionTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Points</label>
            <InputField
              type="number"
              min={1}
              value={questionForm.points}
              onChange={(event) =>
                setQuestionForm((prev) => ({
                  ...prev,
                  points: Number(event.target.value) || 1,
                }))
              }
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm font-medium">Texte de la question</label>
            <textarea
              className="w-full form-input"
              rows={3}
              value={questionForm.question_text}
              onChange={(event) =>
                setQuestionForm((prev) => ({ ...prev, question_text: event.target.value }))
              }
            />
          </div>

          {(questionForm.type === "single_choice" || questionForm.type === "multiple_choice") && (
            <div className="md:col-span-2 space-y-3">
              <label className="text-sm font-medium">Choix de réponses</label>
              <div className="space-y-2">
                {questionForm.choices.map((choice, index) => (
                  <div key={`${index}`} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correct_choice"
                      checked={questionForm.correct_choice_index === index}
                      onChange={() =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          correct_choice_index: index,
                        }))
                      }
                    />
                    <InputField
                      value={choice}
                      onChange={(event) =>
                        setQuestionForm((prev) => ({
                          ...prev,
                          choices: prev.choices.map((item, idx) =>
                            idx === index ? event.target.value : item
                          ),
                        }))
                      }
                    />
                    <Button
                      color="error"
                      disabled={questionForm.choices.length <= 2}
                      onClick={() =>
                        setQuestionForm((prev) => {
                          const nextChoices = prev.choices.filter((_, idx) => idx !== index);
                          const nextCorrect =
                            prev.correct_choice_index >= nextChoices.length
                              ? Math.max(nextChoices.length - 1, 0)
                              : prev.correct_choice_index;
                          return {
                            ...prev,
                            choices: nextChoices,
                            correct_choice_index: nextCorrect,
                          };
                        })
                      }
                    >
                      Suppr.
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                onClick={() =>
                  setQuestionForm((prev) => ({ ...prev, choices: [...prev.choices, ""] }))
                }
              >
                + Ajouter un choix
              </Button>
            </div>
          )}

          {questionForm.type === "short_answer" && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Réponse correcte</label>
              <InputField
                value={questionForm.correct_value}
                onChange={(event) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    correct_value: event.target.value,
                  }))
                }
              />
            </div>
          )}

          {questionForm.type === "number" && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Réponse correcte (nombre)</label>
              <InputField
                type="number"
                value={questionForm.correct_value}
                onChange={(event) =>
                  setQuestionForm((prev) => ({
                    ...prev,
                    correct_value: event.target.value,
                  }))
                }
              />
            </div>
          )}

          <div className="md:col-span-2 flex justify-end gap-2">
            <Button onClick={() => setQuestionForm(defaultQuestionState)}>Réinitialiser</Button>
            <ButtonLoading
              loading={questionLoading}
              label={questionForm.id ? "Mettre à jour la question" : "Ajouter la question"}
              onClick={handleSubmitQuestion}
            />
          </div>
        </div>

        <div className="space-y-3">
          {questions.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune question pour cet exercice.</p>
          ) : null}
          {questions.map((question, index) => (
            <div key={question.id} className="border rounded p-3 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-600">
                    #{index + 1} - {question.type} - {question.points} point(s)
                  </p>
                  <p className="font-medium">{question.question_text || "(Sans texte)"}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => reorderQuestion("up", question)}>↑</Button>
                  <Button onClick={() => reorderQuestion("down", question)}>↓</Button>
                  <Button color="info" onClick={() => editQuestion(question)}>
                    Modifier
                  </Button>
                  <Button color="error" onClick={() => deleteQuestion(question)}>
                    Supprimer
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-gray-50 p-2 rounded overflow-auto">
                  <p className="font-semibold mb-1">Options</p>
                  {(() => {
                    const options = normalizeAsObject(question.options);
                    const choices = Array.isArray(options.choices) ? options.choices : [];
                    if (!choices.length) return <p>-</p>;
                    return (
                      <ul className="list-disc pl-5">
                        {choices.map((choice, idx) => (
                          <li key={`${question.id}-${idx}`}>{String(choice)}</li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
                <div className="bg-gray-50 p-2 rounded overflow-auto">
                  <p className="font-semibold mb-1">Bonne réponse</p>
                  {(() => {
                    const answer = normalizeAsObject(question.correct_answer);
                    if (answer.index !== undefined) return <p>Index: {String(answer.index)}</p>;
                    if (answer.value !== undefined) return <p>{String(answer.value)}</p>;
                    return <p>-</p>;
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
